import { useCallback } from 'react';
import { Button, IconCode2, Wrapper } from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

import { ReadOnlyTooltip } from 'components/context-menu-actions/shared/ReadOnlyTooltip';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  ContentUnion,
  ModuleContainer,
  PageModule,
  PublicationSetting,
  QueryItem,
  UiTitleTreatmentType
} from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import AddQueryModal from './AddQueryModal';
import { getDefaultQuery, getQueryOptions, QueryOption } from './queryUtil';

interface AddQueryProps {
  hierarchyId: string;
  maxAllowedItems: number;
  numTotalItemsUsed: number;
  treatmentType?: string;
  setOpen?: (o: boolean) => void;
  ignoreBaseQuery?: boolean;
  moduleHierarchyId?: string;
  module?: ModuleContainer;
  viewOnly?: boolean;
}

export const AddQuery = ({
  hierarchyId,
  maxAllowedItems,
  numTotalItemsUsed,
  treatmentType,
  setOpen,
  ignoreBaseQuery,
  moduleHierarchyId,
  module,
  viewOnly = false
}: AddQueryProps) => {
  const { data: { publicationSetting } = {} as { publicationSetting: PublicationSetting } } = usePublicationSettings();
  const { modifyEntity, insertEntity, metadata, applyOverfetchForAddedQuery } = useDataModelContext();
  const { setActionModalComponent, setIsModalOpen } = useContextMenuActions();

  const { publicationKey } = metadata;
  const allowAddContent = numTotalItemsUsed < maxAllowedItems;
  const queryOptions = getQueryOptions({
    publicationKey,
    allessehId: metadata.allessehId,
    treatmentType: treatmentType!,
    publicationSetting
  });
  const defaultQuery = getDefaultQuery({
    publicationKey,
    allessehId: metadata.allessehId,
    treatmentType,
    publicationSetting
  });

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setOpen?.(false);
  }, [setIsModalOpen, setOpen]);

  const handleAddQuery = (option: QueryOption) => {
    const query = option.query as Record<string, unknown>;
    const queryItem: QueryItem = {
      type: EntityType.Query,
      uuid: uuidv4(),
      attributes: { id: uuidv4(), query, feedTitle: option.label }
    };
    insertEntity(hierarchyId, queryItem);

    if (moduleHierarchyId) {
      applyOverfetchForAddedQuery(moduleHierarchyId, query);
    }

    handleCloseModal();
  };

  const onPageModuleChange = useCallback(
    (newPageModule: PageModule, collection?: ContentUnion[]) => {
      if (moduleHierarchyId) {
        modifyEntity(moduleHierarchyId, (entity) => {
          const module = entity as ModuleContainer;
          module.attributes.pageModule = newPageModule;
          if (collection) module.collection = collection;
        });
      }
    },
    [moduleHierarchyId, modifyEntity]
  );

  const opensModal = queryOptions.length !== 1 || treatmentType === UiTitleTreatmentType.LeadingAndSupport;

  const handleAddQueryModule = () => {
    if (!opensModal) {
      handleAddQuery(queryOptions[0]);
    } else {
      setIsModalOpen(true);
      setActionModalComponent(
        <AddQueryModal
          count={maxAllowedItems}
          onPageModuleChange={onPageModuleChange}
          pageModule={module?.attributes.pageModule as PageModule}
          hierarchyId={hierarchyId}
          queryOptions={queryOptions}
          maxAllowedItems={maxAllowedItems}
          numTotalItemsUsed={numTotalItemsUsed}
          ignoreBaseQuery={ignoreBaseQuery}
          defaultQuery={defaultQuery}
          onClose={handleCloseModal}
        />
      );
    }
  };

  const isDisabled = !allowAddContent || viewOnly;

  return (
    <Wrapper>
      <ReadOnlyTooltip disabled={viewOnly}>
        <Button
          tertiary
          icon={IconCode2}
          onClick={handleAddQueryModule}
          disabled={isDisabled}
          data-testid="page-draft-add-query"
        >
          Add query
        </Button>
      </ReadOnlyTooltip>
    </Wrapper>
  );
};
